import { baseQueryWithReauth } from './baseQuery';
import { createApi } from '@reduxjs/toolkit/dist/query/react';
import { PatentsResponse, PatentState } from '../redux/types';

export const patentsApi = createApi({
    reducerPath: 'patents',
    baseQuery: baseQueryWithReauth,
    tagTypes: ['Patents', 'PatentStates'],
    endpoints: (builder) => ({
        validatePatents: builder.mutation({
            // trimestre: 1-4 en puertos trimestrales, 1-12 en puertos mensuales (Ushuaia)
            query: (body) => ({
                url: 'patentes/validar-datos',
                method: 'POST',
                body: body
            }),
            transformResponse: (response: any) => response?.data
        }),
        addPatent: builder.mutation({
            // trimestre: 1-4 en puertos trimestrales, 1-12 en puertos mensuales (Ushuaia)
            query: (body) => ({
                url: 'patentes',
                method: 'POST',
                body: body
            })
        }),
        downloadPatentPDF: builder.query({
            query: (patentId) => ({
                url: `patentes/${patentId}/pdf`
            }),
            transformResponse: (response: { data: { pdf: any } }) => response?.data?.pdf
        }),
        getPatents: builder.query<PatentsResponse, { filters: any }>({
            query: ({ filters }) => ({
                url: 'patentes',
                params: filters
            }),
            // AE4-713 — cada fila es una patente en un período: `id` deja de ser único por patente
            // cuando una patente tiene varios períodos (Ushuaia). Se preserva el `id` real de la
            // patente en `idPatente` y se sintetiza un `id` de fila único, necesario porque
            // components/DataTable/MainTable.tsx usa `item.id` como key de React y como criterio
            // de fila seleccionada.
            transformResponse: (response: PatentsResponse): PatentsResponse => ({
                ...response,
                data: {
                    ...response?.data,
                    data: response?.data?.data?.map((item) => ({
                        ...item,
                        idPatente: item?.id,
                        id: item?.idPeriodo ? `${item?.id}_${item?.idPeriodo}` : item?.id
                    }))
                }
            }),
            providesTags: ['Patents']
        }),
        getPatentStates: builder.query<PatentState[], void>({
            query: () => ({
                url: 'patentes/estados'
            }),
            transformResponse: (response: { data: { estados: PatentState[] } }) =>
                response?.data?.estados,
            providesTags: ['PatentStates']
        }),
        getPatent: builder.query({
            query: (id) => ({
                url: `patentes/${id}`
            }),
            transformResponse: (response: { data: { patente: any } }) => response?.data?.patente
        }),
        getAnswers: builder.query({
            query: (patentId) => ({
                url: `patentes/${patentId}/conversacion/respuestas`
            }),
            transformResponse: (response: { data: { respuestas: any } }) =>
                response?.data?.respuestas
        }),
        postPatentAnswer: builder.mutation({
            query: ({ patentId, responseId, body }) => ({
                url: `patentes/${patentId}/conversacion/respuestas/${responseId}`,
                body: body,
                method: 'POST'
            })
        }),
        getConversation: builder.query<any, void>({
            query: (patentId) => `patentes/${patentId}/conversacion`,
            transformResponse: (response: { data: { historial: any } }) => response?.data?.historial
        }),
        unsubscribePatent: builder.mutation({
            query: ({ patentId, body }) => ({
                url: `patentes/${patentId}`,
                method: 'PATCH',
                body: body
            }),
            invalidatesTags: ['Patents']
        }),
        addOperation: builder.mutation({
            query: ({ patentId, body }) => ({
                url: `patentes/${patentId}/estadias`,
                method: 'POST',
                body: body
            })
        }),
        getPatentOperations: builder.query({
            query: ({ patentId, filters }) => ({
                url: `patentes/${patentId}/estadias`,
                params: filters
            })
        }),
        getDockingByPatentId: builder.query({
            query: (patentId) => ({
                url: `patentes/${patentId}/estadias/muelles`
            }),
            transformResponse: (response: { data: { muelles: any } }) => response?.data?.muelles
        }),
        deletePatentMovement: builder.mutation({
            query: ({ patentId, stayId }) => ({
                url: `patentes/${patentId}/estadias/${stayId}`,
                method: 'DELETE'
            })
        }),
        editPatentMovement: builder.mutation({
            query: ({ patentId, stayId, body }: any) => ({
                url: `patentes/${patentId}/estadias/${stayId}`,
                method: 'PATCH',
                body: body
            })
        }),
        patentBillingRecord: builder.mutation({
            query: ({ patentId, formData }) => ({
                url: `patentes/${patentId}/facturacion`,
                method: 'POST',
                body: formData
            })
        }),
        downloadAttachedFile: builder.query({
            query: ({ patentId, billingNumber }) => ({
                url: `patentes/${patentId}/facturacion/${billingNumber}`
            }),
            transformResponse: (response: { data: { presignedUrl: string } }) =>
                response?.data?.presignedUrl
        }),
        getPatentRateCodes: builder.query({
            query: () => ({
                url: 'patentes/codigos-tarifa'
            }),
            transformResponse: (response: { data: { codigos: any } }) => response?.data?.codigos
        }),
        liquidatePatent: builder.mutation({
            query: ({ patentId, idPeriodo }: { patentId: string | number; idPeriodo: string | number }) => ({
                url: `patentes/${patentId}/liquidacion`,
                method: 'POST',
                body: { idPeriodo }
            })
        }),
        downloadLiquidationPDF: builder.query({
            query: (patentId) => ({
                url: `patentes/${patentId}/pasavante/pdf`
            }),
            transformResponse: (response: { data: { pdf: any } }) => response?.data?.pdf
        })
    })
});

export const {
    useLazyDownloadLiquidationPDFQuery,
    useLiquidatePatentMutation,
    useGetPatentStatesQuery,
    useGetPatentRateCodesQuery,
    useLazyDownloadAttachedFileQuery,
    usePatentBillingRecordMutation,
    useEditPatentMovementMutation,
    useDeletePatentMovementMutation,
    useGetDockingByPatentIdQuery,
    useGetPatentOperationsQuery,
    useAddOperationMutation,
    useUnsubscribePatentMutation,
    useLazyGetConversationQuery,
    usePostPatentAnswerMutation,
    useGetAnswersQuery,
    useGetPatentQuery,
    useLazyGetPatentQuery,
    useGetPatentsQuery,
    useLazyDownloadPatentPDFQuery,
    useValidatePatentsMutation,
    useAddPatentMutation
} = patentsApi;
